import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

interface User {
  id: string;
  email: string;
  name: string;
  role: 'owner' | 'admin' | 'member';
}

interface Team {
  id: string;
  name: string;
  members: User[];
}

interface AuthStore {
  user: User | null;
  team: Team | null;
  isAuthenticated: boolean;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  addTeamMember: (member: Omit<User, 'id'>) => void;
  removeTeamMember: (id: string) => void;
  updateTeamMember: (id: string, updates: Partial<User>) => void;
}

const defaultUser: User = {
  id: '1',
  email: 'demo@example.com',
  name: 'Demo User',
  role: 'owner',
};

const defaultTeam: Team = {
  id: '1',
  name: 'Demo Team',
  members: [defaultUser],
};

const initialState = {
  user: defaultUser,
  team: defaultTeam,
  isAuthenticated: true,
};

export const useAuthStore = create<AuthStore>()(
  persist(
    (set) => ({
      ...initialState,
      login: async (email, password) => {
        set({ user: defaultUser, team: defaultTeam, isAuthenticated: true });
      },
      logout: () => {
        set({ user: null, team: null, isAuthenticated: false });
      },
      addTeamMember: (member) => {
        set((state) => ({
          team: state.team ? {
            ...state.team,
            members: [...state.team.members, { ...member, id: crypto.randomUUID() }],
          } : null,
        }));
      },
      removeTeamMember: (id) => {
        set((state) => ({
          team: state.team ? {
            ...state.team,
            members: state.team.members.filter((m) => m.id !== id),
          } : null,
        }));
      },
      updateTeamMember: (id, updates) => {
        set((state) => ({
          team: state.team ? {
            ...state.team,
            members: state.team.members.map((m) => 
              m.id === id ? { ...m, ...updates } : m
            ),
          } : null,
        }));
      },
    }),
    {
      name: 'auth-store',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        user: state.user,
        team: state.team,
        isAuthenticated: state.isAuthenticated,
      }),
      version: 2,
      migrate: (persistedState: any, version: number) => {
        if (version === 1) {
          return {
            ...initialState,
            ...persistedState,
          };
        }
        return persistedState;
      },
    }
  )
);